import type { Lang } from '@/i18n';
import type { ProviderStartPayload } from '@/lib/api/auth';

type TelegramResult = { id_token?: string; error?: string };
type GoogleCredential = { credential?: string };
type GoogleMoment = {
  isNotDisplayed?: () => boolean;
  isSkippedMoment?: () => boolean;
  isDismissedMoment?: () => boolean;
  getDismissedReason?: () => string;
};

declare global {
  interface Window {
    Telegram?: {
      Login?: {
        auth: (
          options: { client_id: number; scope: string[]; lang: string; nonce: string },
          callback: (result: TelegramResult) => void,
        ) => void;
      };
    };
    google?: {
      accounts?: {
        id?: {
          initialize: (options: {
            client_id: string;
            nonce: string;
            ux_mode: 'popup';
            auto_select: boolean;
            cancel_on_tap_outside: boolean;
            callback: (result: GoogleCredential) => void;
          }) => void;
          prompt: (callback?: (moment: GoogleMoment) => void) => void;
        };
      };
    };
  }
}

const SDK_TIMEOUT = 12_000;
const PROVIDER_TIMEOUT = 120_000;

function loadScript(id: string, src: string, ready: () => boolean) {
  if (ready()) return Promise.resolve();
  return new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(id) as HTMLScriptElement | null;
    const script = existing ?? document.createElement('script');
    let settled = false;
    const done = (ok: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      ok && ready() ? resolve() : reject(new Error('provider_unavailable'));
    };
    const timer = window.setTimeout(() => done(false), SDK_TIMEOUT);
    script.addEventListener('load', () => done(true), { once: true });
    script.addEventListener('error', () => done(false), { once: true });
    if (!existing) {
      script.id = id;
      script.src = src;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });
}

export async function googleCredential(flow: ProviderStartPayload): Promise<string> {
  await loadScript('orca-google-identity', 'https://accounts.google.com/gsi/client', () => !!window.google?.accounts?.id);
  const gis = window.google?.accounts?.id;
  if (!gis) throw new Error('provider_unavailable');
  return new Promise<string>((resolve, reject) => {
    let settled = false;
    let timer = 0;
    const fail = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      reject(new Error('provider_cancelled'));
    };
    timer = window.setTimeout(fail, PROVIDER_TIMEOUT);
    gis.initialize({
      client_id: flow.client_id,
      nonce: flow.nonce,
      ux_mode: 'popup',
      auto_select: false,
      cancel_on_tap_outside: true,
      callback: ({ credential }) => {
        if (settled) return;
        if (!credential) return fail();
        settled = true;
        window.clearTimeout(timer);
        resolve(credential);
      },
    });
    gis.prompt((moment) => {
      const dismissed = moment.isDismissedMoment?.();
      const dismissalIsFailure = dismissed && moment.getDismissedReason?.() !== 'credential_returned';
      if (moment.isNotDisplayed?.() || moment.isSkippedMoment?.() || dismissalIsFailure) {
        window.setTimeout(fail, 0);
      }
    });
  });
}

export async function telegramCredential(flow: ProviderStartPayload, language: Lang): Promise<string> {
  await loadScript('orca-telegram-login', 'https://telegram.org/js/telegram-login.js', () => !!window.Telegram?.Login);
  const login = window.Telegram?.Login;
  const clientId = Number(flow.client_id);
  if (!login || !Number.isSafeInteger(clientId) || clientId <= 0) throw new Error('provider_unavailable');
  return new Promise<string>((resolve, reject) => {
    let settled = false;
    let timer = 0;
    const finish = (result?: TelegramResult) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      if (result?.id_token) resolve(result.id_token);
      else reject(new Error(result?.error ? 'provider_failed' : 'provider_cancelled'));
    };
    timer = window.setTimeout(() => finish(), PROVIDER_TIMEOUT);
    login.auth({ client_id: clientId, scope: ['profile'], lang: language, nonce: flow.nonce }, (result) => {
      finish(result);
    });
  });
}
